import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './NewsDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://saskararecord.com/api';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function NewsDetail() {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API_BASE}/news/public/${slug}`)
      .then((res) => {
        if (mounted) setNews(res.data.data);
      })
      .catch(() => {
        if (mounted) setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="news-detail__state">Memuat...</div>;
  }

  if (notFound || !news) {
    return (
      <div className="news-detail__state">
        <p>Berita tidak ditemukan.</p>
        <Link to="/" className="news-detail__back">← Kembali ke Beranda</Link>
      </div>
    );
  }

  // Kumpulkan hanya foto yang benar-benar ada (gak reserve slot kosong)
  const photos = [news.thumbnail_url, news.photo_url_2, news.photo_url_3].filter(Boolean);

  // Pecah konten jadi paragraf berdasarkan baris kosong
  const paragraphs = (news.content || '').split(/\n\s*\n/).filter((p) => p.trim());

  // Selipkan foto di antara paragraf kalau ada lebih dari 1 foto,
  // biar teks & galeri gak numpuk semua di atas
  const renderContentWithPhotos = () => {
    if (photos.length <= 1) {
      // 1 foto atau tanpa foto: foto besar di atas, teks mengalir normal di bawah
      return (
        <>
          {photos[0] && (
            <div className="news-detail__photo news-detail__photo--single">
              <img src={photos[0]} alt={news.title} />
            </div>
          )}
          <div className="news-detail__content">
            {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </>
      );
    }

    // 2-3 foto: galeri grid di atas (menyesuaikan jumlah), lalu teks di bawah
    return (
      <>
        <div className={`news-detail__gallery news-detail__gallery--${photos.length}`}>
          {photos.map((url, i) => (
            <div className="news-detail__gallery-item" key={i}>
              <img src={url} alt={`${news.title} - foto ${i + 1}`} />
            </div>
          ))}
        </div>
        <div className="news-detail__content">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </>
    );
  };

  const pageTitle = `${news.title} — Saskara Record`;
  const pageDesc = (news.content || '').replace(/<[^>]+>/g, '').slice(0, 160) || 'Berita terbaru dari Saskara Record.';
  const pageImage = news.thumbnail_url || 'https://saskararecord.com/logo512.png';
  const pageUrl = `https://saskararecord.com/news/${slug}`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: news.title,
          image: news.thumbnail_url || undefined,
          datePublished: news.published_at || undefined,
          url: pageUrl,
        })}
      </script>
    <div className="news-detail">
      <nav className="news-detail__nav">
        <Link to="/" className="news-detail__back">← Beranda</Link>
      </nav>

      <article className="news-detail__article">
        <div className="news-detail__date">{formatDate(news.published_at)}</div>
        <h1 className="news-detail__title">{news.title}</h1>

        {renderContentWithPhotos()}
      </article>
    </div>
    </>
  );
}
